// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// devextreme imports
import notify from 'devextreme/ui/notify';
import { DxScrollViewComponent } from 'devextreme-angular';

// CC-Common imports
import { CCLocalStorageCategory, CCLocalStorageService, MessageBoxComponent, IconConstants, Utils } from 'cc-common';

// constants imports
import { Constants } from '../../../_constants/constants';
import { StorageKeyConstants } from '../../../_constants/storage-key-constants';
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';
import { MessageInterval } from '../../../_constants/messages-intervals';
import { WorkflowConstants } from '../../../shared-workflow-module/_constants/worflow-constants';

// component imports
import { WorkflowPaymentRequestSimpleComponent } from '../workflow-payment-request-simple/workflow-payment-request-simple.component';
import { WorkflowPaymentRequestDetailsComponent } from '../workflow-payment-request-details/workflow-payment-request-details.component';
import { WorkflowPaymentRequestToolbarComponent } from '../workflow-payment-request-toolbar/workflow-payment-request-toolbar.component';

// Service imports
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { WorkflowPaymentRequestService } from '../../_services/workflow-payment-request.service';

// model imports
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { StatusMessageResultDto } from '../../../_models/status-message-result-dto';
import { AddPaymentRequestParameterDto } from '../../_models/add-payment-request-parameter-dto';
import { WFEVendorResultDto } from '../../../shared-workflow-module/_models/wfe-vendor-result-dto';

@Component({
    selector: 'app-workflow-payment-request',
    templateUrl: './workflow-payment-request.component.html',
    styleUrls: ['./workflow-payment-request.component.scss']
})
export class WorkflowPaymentRequestComponent implements OnInit {
    @ViewChild('submitInvoice', { static: false }) submitInvoice: WorkflowPaymentRequestSimpleComponent;
    @ViewChild('paymentDetails', { static: false }) paymentDetails: WorkflowPaymentRequestDetailsComponent;
    @ViewChild('paymentToolbar', { static: false }) paymentToolbar: WorkflowPaymentRequestToolbarComponent;
    @ViewChild('workflowSubmitInvoiceForm', { static: false }) workflowSubmitInvoiceForm: MessageBoxComponent;
    @ViewChild('paymentInfoScrollView', { static: false }) paymentInfoScrollView: DxScrollViewComponent;

    @Input() isPopup = false;
    @Input() communityId: number;
    @Input() vendorCode: string;
    @Input() isPTPaymentRequest = false;

    @Output() onClose = new EventEmitter<boolean>();
    @Output() onPRFinish = new EventEmitter<number>();

    pdfViewerToLeft = false;
    pdfOrder: number;
    invoiceOrder: number;

    pdfURL: string = null;
    pdfLoader: boolean;

    formPanelWidth: string;
    pdfViewerWidth: string;

    separateWindow = false;
    helpLink: string;

    invoiceOption: string;
    community: CommunitySelectorResultDto;

    showToolbar = false;
    selectedVendor: WFEVendorResultDto;
    boardApproved = false;

    isProcessingDisabled = true;
    isSubmitRequestProcessed = false;

    iconClose = IconConstants.CLOSE;
    iconReset = IconConstants.RESET;
    iconStar = IconConstants.STAR;
    iconHelp = IconConstants.HELP;

    showAdvanceMode = false;
    loadingVisible = false;
    workOrderIds: Array<number> = [];

    constructor(
        private readonly localStorageService: CCLocalStorageService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly workflowPaymentRequestService: WorkflowPaymentRequestService,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.cbAPSubmitInvoice);
    }

    ngOnInit() {
        this.initializeDefaultConfiguration();
        this.getSplitterPanelWidth();

        setInterval(() => {
            this.isProcessingDisabled = Utils.isFalse(this.checkMandatoryFields());
        }, 500);
    }

    onSplitterDragStart(): void {
        this.pdfLoader = true;
    }

    dragEndPdfLeft(event): void {
        this.pdfLoader = false;
        const sizes = event.sizes;

        this.pdfViewerWidth = sizes[this.pdfOrder - 1];
        this.formPanelWidth = sizes[this.invoiceOrder - 1];

        this.setSplitterPanelWidth();
    }

    onSplitterPositionChange(pdfToLeft: boolean): void {
        setTimeout(() => {
            this.pdfViewerToLeft = pdfToLeft;
            if (Utils.isTrue(this.pdfViewerToLeft)) {
                this.pdfOrder = 2;
                this.invoiceOrder = 1;
            } else {
                this.pdfOrder = 1;
                this.invoiceOrder = 2;
            }
            this.setSplitterContentPosition();
        }, 100);
    }

    onHideSplitter(separateWindow: boolean): void {
        setTimeout(() => {
            this.separateWindow = separateWindow;

            if (Utils.isTrue(this.separateWindow)) {
                this.invoiceOrder = 0;
                this.pdfOrder = 1;
            } else {
                if (Utils.isTrue(this.pdfViewerToLeft)) {
                    this.pdfOrder = 2;
                    this.invoiceOrder = 1;
                } else {
                    this.pdfOrder = 1;
                    this.invoiceOrder = 2;
                }
            }
        }, 100);
    }

    onInvoiceSelection(event: any): void {
        this.pdfLoader = true;
        this.pdfURL = event;

        setTimeout(() => {
            this.pdfLoader = false;
        }, 350);
    }

    onInvoiceOptionChange(event: any): void {
        this.invoiceOption = event;
    }

    onCommunitySelectionChange(community: CommunitySelectorResultDto): void {
        this.community = community;
        if (Utils.isNullOrUndefined(this.community)) {
            this.resetPaymentRequest();
        }
    }

    onResetClick(): void {
        this.resetPaymentRequest();
    }

    onSubmitProcessing(): void {
        const resultStatus = this.checkRequiredField();
        if (Utils.isTrue(resultStatus.isValid)) {
            if (Utils.equalsIgnoreCase(this.invoiceOption, 'generate_payment_request') || this.showAdvanceMode) {
                this.uploadAdvancePaymentRequest();
            } else {
                this.uploadVendorInvoice();
            }
        } else if (Utils.notNullAndDefined(resultStatus.message)) {
            this.workflowSubmitInvoiceForm.showErrorMessage('Error', resultStatus.message);
        }
    }

    onVendorPayeeSelection(vendor: WFEVendorResultDto): void {
        setTimeout(() => {
            this.selectedVendor = vendor;
            this.showToolbar = Utils.notNullAndDefined(vendor);
        }, 100);
    }

    isBoardApproved(boardApproved: boolean): void {
        setTimeout(() => (this.showToolbar = true), 500);
        this.boardApproved = boardApproved;
    }

    onPaymentRequestClose(): void {
        this.onClose.emit(this.isSubmitRequestProcessed);
    }

    onAdvanceModeSelectionChange(showAdvanceMode: boolean): void {
        this.showAdvanceMode = showAdvanceMode;
    }

    onWorkOrderSelection(workOrderIds: Array<number>): void {
        this.workOrderIds = workOrderIds;
    }

    private getSplitterPanelWidth(): void {
        this.formPanelWidth =
            this.localStorageService.getItem(
                StorageKeyConstants.wfEditorFormPanelWidthInPercent,
                CCLocalStorageCategory.WFE
            ) || Constants.splitterFormWidth;
        this.pdfViewerWidth =
            this.localStorageService.getItem(
                StorageKeyConstants.wfInvoicePanelWidthInPercent,
                CCLocalStorageCategory.WFE
            ) || Constants.splitterPDFViewerWidth;

        this.setSplitterPanelWidth();
    }

    private setSplitterPanelWidth(): void {
        this.localStorageService.setItem(
            StorageKeyConstants.wfEditorFormPanelWidthInPercent,
            this.formPanelWidth,
            CCLocalStorageCategory.WFE
        );
        this.localStorageService.setItem(
            StorageKeyConstants.wfInvoicePanelWidthInPercent,
            this.pdfViewerWidth,
            CCLocalStorageCategory.WFE
        );
    }

    private setSplitterContentPosition(): void {
        this.localStorageService.setItem(
            StorageKeyConstants.wfPaymentRequestPosition,
            this.pdfViewerToLeft.toString(),
            CCLocalStorageCategory.WFE
        );
    }

    private initializeDefaultConfiguration(): void {
        this.invoiceOrder = 1;
        this.pdfOrder = 2;
    }

    private checkMandatoryFields(): boolean {
        let result = false;
        let submitInvoiceCheck = false;
        let advancePaymentCheck = false;
        let uploadFileCheck = false;
        if (Utils.notNullAndDefined(this.submitInvoice)) {
            const paymentEditor = this.submitInvoice.getPaymentEditor();
            if (
                Utils.notNullAndDefined(paymentEditor) &&
                Utils.notNullAndDefined(paymentEditor.communityId) &&
                Utils.notEquals(paymentEditor.specialHandlingId, -1)
            ) {
                submitInvoiceCheck = true;
                if (Utils.isTrue(this.submitInvoice.isNotesMandatory())) {
                    submitInvoiceCheck = Utils.notNullAndDefined(paymentEditor.specialInstruction) ? true : false;
                }
            }
            if (
                Utils.notNullAndDefined(paymentEditor) &&
                Utils.notNullAndDefined(paymentEditor.fileName) &&
                Utils.notNullAndDefined(paymentEditor.fileBase64String)
            ) {
                uploadFileCheck = true;
            }
        }

        if (Utils.equalsIgnoreCase(this.invoiceOption, 'generate_payment_request') || this.showAdvanceMode) {
            if (Utils.notNullAndDefined(this.paymentDetails)) {
                const paymentDetails = this.paymentDetails.getAdvancePaymentRequestDetail();
                if (
                    Utils.notNullAndDefined(paymentDetails) &&
                    Utils.notNullAndDefined(paymentDetails.payeeType) &&
                    Utils.notNullAndDefined(paymentDetails.payeeCode) &&
                    Utils.notNullAndDefined(paymentDetails.invoiceDate) &&
                    Utils.notNullAndDefined(paymentDetails.dueDate) &&
                    Utils.notNullAndDefined(paymentDetails.invoiceAmount) &&
                    Utils.notNullAndDefined(paymentDetails.invoiceNumber) &&
                    Utils.notNullAndDefined(paymentDetails.bankAccountId)
                ) {
                    if (
                        (Utils.isTrue(paymentDetails.isVendorAccountMandatory) &&
                            Utils.isNullOrUndefined(paymentDetails.vendorAccountId)) ||
                        (Utils.equalsIgnoreCase(paymentDetails.payeeCode, 'RM_NOTINLIST') &&
                            Utils.isNullOrEmpty(paymentDetails.vendorInfo))
                    ) {
                        advancePaymentCheck = false;
                    } else {
                        advancePaymentCheck = true;
                    }
                }
            }
        }
        if (Utils.equalsIgnoreCase(this.invoiceOption, 'upload_vendor_invoice')) {
            result = Utils.isTrue(submitInvoiceCheck) && Utils.isTrue(uploadFileCheck);
            if (Utils.isTrue(this.showAdvanceMode)) {
                result = result && advancePaymentCheck;
            }
        } else {
            result = submitInvoiceCheck && advancePaymentCheck;
        }

        return result;
    }

    private checkRequiredField(): any {
        const resultStatus = { isValid: true, message: null };
        if (Utils.notNullAndDefined(this.submitInvoice)) {
            const paymentEditor = this.submitInvoice.getPaymentEditor();
            if (Utils.notNullAndDefined(paymentEditor)) {
                if (Utils.isNullOrUndefined(paymentEditor.communityId)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Community is required.';
                } else if (
                    Utils.isNullOrUndefined(paymentEditor.specialHandlingId) &&
                    Utils.equals(paymentEditor.specialHandlingId, -1)
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Special Handling is required.';
                } else if (
                    Utils.isTrue(this.submitInvoice.isNotesMandatory()) &&
                    Utils.isNullOrUndefined(paymentEditor.specialInstruction)
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Special Instructions are required.';
                } else if (
                    Utils.equalsIgnoreCase(this.invoiceOption, 'upload_vendor_invoice') &&
                    (Utils.isNullOrUndefined(paymentEditor.fileName) ||
                        Utils.isNullOrUndefined(paymentEditor.fileBase64String))
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Invoice is mandatory.';
                }
            }
        }
        if (
            (Utils.equalsIgnoreCase(this.invoiceOption, 'generate_payment_request') || this.showAdvanceMode) &&
            Utils.isTrue(resultStatus.isValid)
        ) {
            const paymentDetails = this.paymentDetails.getAdvancePaymentRequestDetail();

            if (Utils.notNullAndDefined(paymentDetails)) {
                if (Utils.isNullOrUndefined(paymentDetails.payeeType)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Payee Type is required.';
                } else if (
                    Utils.equalsIgnoreCase(paymentDetails.payeeType, WorkflowConstants.PAYEE_TYPE_OWNER) &&
                    Utils.isNullOrUndefined(paymentDetails.printedOwnerName)
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Printed Owner Name is required.';
                }

                if (Utils.isNullOrUndefined(paymentDetails.payeeCode)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Payee Code is required.';
                } else if (Utils.isNullOrUndefined(paymentDetails.invoiceDate)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Invoice Date is required.';
                } else if (Utils.isNullOrUndefined(paymentDetails.dueDate)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Due Date is required.';
                } else if (Utils.isNullOrUndefined(paymentDetails.invoiceAmount)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Invoice Amount is required.';
                } else if (Utils.isNullOrUndefined(paymentDetails.invoiceNumber)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Invoice Number is required.';
                } else if (Utils.isNullOrUndefined(paymentDetails.bankAccountId)) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Bank Account is required.';
                } else if (Utils.isFalse(this.paymentDetails.isLineItemsValid())) {
                    resultStatus.isValid = false;
                } else if (
                    Utils.equalsIgnoreCase(paymentDetails.payeeCode, 'RM_NOTINLIST') &&
                    Utils.isNullOrEmpty(paymentDetails.vendorInfo)
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'The selected Payee must have Vendor Info.';
                } else if (
                    Utils.isTrue(paymentDetails.isVendorAccountMandatory) &&
                    Utils.isNullOrUndefined(paymentDetails.vendorAccountId)
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = 'Vendor Account is required for selected Payee';
                }
            }
        }
        return resultStatus;
    }

    private uploadVendorInvoice(): void {
        this.loadingVisible = true;
        const paymentEditor = this.submitInvoice.getPaymentEditor();
        this.workflowPaymentRequestService.submitInvoice(paymentEditor).subscribe(
            (result: StatusMessageResultDto) => {
                this.showPaymentRequestSuccessMessage(result);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private uploadAdvancePaymentRequest(): void {
        const paymentEditor = this.submitInvoice.getPaymentEditor();
        const paymentDetails = this.paymentDetails.getAdvancePaymentRequest();
        paymentDetails.generatePaymentRequest = Utils.equalsIgnoreCase(this.invoiceOption, 'generate_payment_request');
        paymentDetails.communityId = paymentEditor.communityId;
        paymentDetails.specialHandlingId = paymentEditor.specialHandlingId;
        paymentDetails.specialInstruction = paymentEditor.specialInstruction;
        paymentDetails.amount = paymentDetails.invoiceAmount;
        if (Utils.notNullAndDefined(paymentEditor.fileBase64String)) {
            paymentDetails.fileBase64String = paymentEditor.fileBase64String;
            paymentDetails.fileName = paymentEditor.fileName;
        }

        const baseParams: AddPaymentRequestParameterDto = new AddPaymentRequestParameterDto();
        baseParams.paymentRequest = paymentDetails;
        baseParams.workOrderIds = this.workOrderIds;

        this.loadingVisible = true;
        this.workflowPaymentRequestService.submitAdvancePayment(baseParams).subscribe(
            (result: StatusMessageResultDto) => {
                this.showPaymentRequestSuccessMessage(result);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private showPaymentRequestSuccessMessage(result: StatusMessageResultDto): void {
        if (Utils.isTrue(result.isSuccessMessage)) {
            const workflowId = result.message.match(/(\d+)/);
            if (Utils.isTrue(this.isPTPaymentRequest)) {
                this.helperService
                    .getOkDialog('Information', `AAP WorkflowId # ${workflowId[0]} created fo you.`)
                    .show()
                    .then(() => {
                        this.onPRFinish.emit(Number(workflowId[0]));
                    });
            } else {
                this.workflowSubmitInvoiceForm.showInfoMessage('Success', result.message, () => {
                    this.isSubmitRequestProcessed = true;
                    setTimeout(() => this.resetPaymentRequest(), 250);
                });
            }
        } else {
            notify(result.message, 'Error', MessageInterval.errorMsgInterval);
        }
    }

    private resetPaymentRequest(): void {
        if (Utils.notNullAndDefined(this.submitInvoice)) {
            this.submitInvoice.resetEditor();
        }

        if (Utils.notNullAndDefined(this.paymentDetails)) {
            this.paymentDetails.resetDetails();
        }

        if (Utils.notNullAndDefined(this.paymentToolbar)) {
            this.paymentToolbar.showAdvanceMode = Utils.equalsIgnoreCase(
                this.invoiceOption,
                'generate_payment_request'
            );
        }

        this.selectedVendor = null;
        this.pdfURL = null;
        this.community = null;
        this.showToolbar = false;
        this.boardApproved = false;

        this.paymentInfoScrollView.instance.scrollTo(0);
    }
}
